'use client'

// Server-rendered markup that React never hydrates.
//
// The behmn pages are driven by the site's own scripts (public/assets/js),
// which rewrite parts of the DOM: they wrap words, fill the intro film, swap
// the Contact intent. If React hydrated that markup, the scripts would have to
// wait for hydration (the black screen before the intro film) or React would
// find a DOM it didn't render and throw it away. So the server renders the
// children to HTML as usual, and when React hydrates, this component hands the
// element's own current HTML back as dangerouslySetInnerHTML. React sees
// nothing to change and keeps the live nodes, so the scripts run as plain
// deferred scripts, at the same moment they did in the approved static build.
// (An empty __html does not work: React 19 clears the children.)
import { useId, useLayoutEffect, useRef, type ReactNode } from 'react'

const RELOADED = 'behmn:reloaded'
// Read before React renders anything: Next's client-rendered error document
// (a platform page called notFound()) is <html id="__next_error__">.
const ERROR_SHELL = typeof document !== 'undefined' && document.documentElement.id === '__next_error__'

export function StaticIsland({ className, children }: { className?: string; children: ReactNode }) {
  const id = useId()
  // The island's HTML as it stands in the document when React first renders:
  // null on the server, and null when there is no server-rendered element.
  const kept = useRef<string | null | undefined>(undefined)
  if (kept.current === undefined) {
    const el = typeof document === 'undefined' ? null : document.querySelector(`[data-island="${CSS.escape(id)}"]`)
    kept.current = el ? el.innerHTML : null
  }

  // Rendered from scratch (no server HTML to keep) means one of two things:
  //  - a client-side navigation (a <Link> somewhere in the platform): the
  //    deferred scripts never ran, so load the page as a document, which is
  //    how every marketing link works. Once only, so it can never loop.
  //  - Next's client-rendered error document: a reload would give the same
  //    thing, so the children render as ordinary React output and the page
  //    reads in its no-script state.
  useLayoutEffect(() => {
    try {
      if (kept.current !== null) return sessionStorage.removeItem(RELOADED) // a real document load
      if (ERROR_SHELL || sessionStorage.getItem(RELOADED) === location.href) return
      sessionStorage.setItem(RELOADED, location.href)
    } catch {
      return
    }
    window.location.reload()
  }, [])

  if (kept.current != null) {
    return <div data-island={id} className={className} suppressHydrationWarning dangerouslySetInnerHTML={{ __html: kept.current }} />
  }
  return (
    <div data-island={id} className={className}>
      {children}
    </div>
  )
}
